/**
 * Centralised keyboard shortcut handler.
 *
 * Pass a map of key bindings; the hook attaches a single
 * `keydown` listener at the document level for the lifetime of the
 * component and dispatches to handlers by parsed key string.
 *
 * Key syntax (case-insensitive):
 *   - Single char: `'k'`, `'/'`, `'?'`
 *   - With modifiers: `'cmd+k'`, `'shift+/'`, `'ctrl+enter'`
 *     - `cmd` is ⌘ on macOS and Ctrl on Windows/Linux
 *     - `meta` is the OS-native super key (matches macOS ⌘ only)
 *     - `ctrl` matches Control on every platform
 * Special names: `'enter'`, `'esc'`, `'space'`, `'tab'`, `'up'`,
 *   `'down'`, `'left'`, `'right'`, `'backspace'`, `'delete'`.
 *
 * **Input-field policy.** When focus is in an `<input>`, `<textarea>`,
 * or `[contenteditable]`, single-character shortcuts are ignored —
 * typing `j` in the composer should not jump to the next ticket.
 * Combos with modifiers (`cmd+k`, `cmd+enter`) still fire because
 * users explicitly pressed a modifier. Per-handler override available
 * via `inInput: true` for the rare always-fire shortcut.
 *
 * Per INTERACTION_SPEC.md §10 — the canonical shortcut map.
 */

import { useEffect } from 'react';

export interface HotkeyHandler {
  /** Called when the shortcut fires. preventDefault is called for you. */
  (event: KeyboardEvent): void;
}

export interface HotkeyOptions {
  /** Allow the shortcut to fire even when an input/textarea/contenteditable
   *  has focus. Default: false (recommended for nav shortcuts). Use
   *  `true` for shortcuts deliberately bound to in-field actions
   *  (e.g. ⌘↵ to send in the composer is bound there directly). */
  inInput?: boolean;
  /** When true the hook is inert. Useful for gating shortcuts on a
   *  drawer/modal being open without unmounting the hook. */
  disabled?: boolean;
}

export type HotkeyMap = Record<string, HotkeyHandler>;

/** Normalise a parsed key descriptor for stable comparison. */
interface ParsedHotkey {
  key: string;
  cmd: boolean; // ⌘ on macOS, Ctrl on win/linux
  meta: boolean; // macOS ⌘ specifically
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
}

const SPECIAL_KEY_ALIAS: Record<string, string> = {
  enter: 'enter',
  return: 'enter',
  esc: 'escape',
  escape: 'escape',
  space: ' ',
  tab: 'tab',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  backspace: 'backspace',
  delete: 'delete',
};

function parseHotkey(spec: string): ParsedHotkey {
  const tokens = spec
    .toLowerCase()
    .split('+')
    .map((s) => s.trim())
    .filter(Boolean);
  const out: ParsedHotkey = {
    key: '',
    cmd: false,
    meta: false,
    ctrl: false,
    shift: false,
    alt: false,
  };
  for (const t of tokens) {
    if (t === 'cmd') out.cmd = true;
    else if (t === 'meta') out.meta = true;
    else if (t === 'ctrl') out.ctrl = true;
    else if (t === 'shift') out.shift = true;
    else if (t === 'alt' || t === 'option') out.alt = true;
    else out.key = SPECIAL_KEY_ALIAS[t] ?? t;
  }
  return out;
}

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

function eventMatches(parsed: ParsedHotkey, event: KeyboardEvent): boolean {
  if (parsed.key !== event.key.toLowerCase()) return false;

  // `cmd` = ⌘ on macOS or Ctrl on win/linux. Matches whichever fired.
  if (parsed.cmd) {
    if (isMac ? !event.metaKey : !event.ctrlKey) return false;
  } else if (parsed.meta) {
    if (!event.metaKey) return false;
  } else if (parsed.ctrl) {
    if (!event.ctrlKey) return false;
  } else {
    // No modifier in spec → reject if a non-shift modifier is held,
    // otherwise `j` would fire on `cmd+j` too. Shift IS allowed for
    // the "?"-key family where shift is intrinsic to producing the char.
    if (event.metaKey || event.ctrlKey || event.altKey) return false;
  }

  if (parsed.shift && !event.shiftKey) return false;
  if (parsed.alt && !event.altKey) return false;

  return true;
}

/** Returns true if focus is in a form element where typing single
 *  characters should not trigger nav shortcuts. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Bind a set of keyboard shortcuts. Re-runs (re-binds) when `bindings`
 * changes; pass a stable reference (`useMemo`) if you want a steady
 * listener.
 */
export function useHotkeys(
  bindings: HotkeyMap,
  options: HotkeyOptions = {}
): void {
  const { inInput = false, disabled = false } = options;
  useEffect(() => {
    if (disabled) return;
    const parsed = Object.entries(bindings).map(
      ([spec, handler]) => [parseHotkey(spec), handler] as const
    );

    function onKeyDown(event: KeyboardEvent) {
      const editable = isEditableTarget(event.target);
      for (const [p, handler] of parsed) {
        if (!eventMatches(p, event)) continue;
        // Modifier-bearing combos always fire. Bare keys defer to the
        // editable rule unless inInput=true overrides.
        const hasModifier = p.cmd || p.meta || p.ctrl || p.alt;
        if (editable && !hasModifier && !inInput) continue;
        event.preventDefault();
        handler(event);
        return;
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [bindings, disabled, inInput]);
}
